import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import { ImportPanel } from "@/features/import/ImportPanel";
import { siteListQuery, structureKeys, warehouseDetailQuery, warehouseListQuery, warehouseTypes, type WarehouseRow } from "@/features/warehousing/api";
import { WarehousePanel, warehouseTypeTone } from "@/features/warehousing/WarehousePanel";
import { useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { useCan, useSessionContext } from "@/lib/session";
import { warehouseListColumns } from "@patronix/domain/list";
import { filteredQuery, type ClientColumn } from "@/lib/clientList";
import { listView, viewSearch, type ViewSearch } from "@/lib/listView";

// Warehouses of the organization (all companies), one page: a tenant has
// dozens at most. One request for the list (embeds site and company codes),
// one per detail (prefetched on hover). Sites load once, for the create form.
// Sites and warehouses of the active company are imported here (ADR-034).

interface Search extends ViewSearch {
  inactive: boolean;
  warehouse?: string | undefined;
  /** The import panel is open (replaces the warehouse detail). */
  import?: "sites" | "warehouses" | undefined;
}

const invalidateAfterImport = [structureKeys.all] as const;

const clientColumns: ClientColumn<WarehouseRow>[] = [
  { key: "code", type: "text", value: (r) => r.code },
  { key: "name", type: "text", value: (r) => r.name },
  { key: "company", type: "text", value: (r) => r.company?.code },
  { key: "site", type: "text", value: (r) => (r.site ? `${r.site.code} ${r.site.name}` : null) },
  { key: "type", type: "enum", value: (r) => r.warehouse_type },
  { key: "negative", type: "bool", value: (r) => r.allow_negative },
];

export const Route = createFileRoute("/_app/warehousing/warehouses")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = { inactive: raw.inactive === true || raw.inactive === "true" };
    if (typeof raw.warehouse === "string" && raw.warehouse) s.warehouse = raw.warehouse;
    if (raw.import === "sites" || raw.import === "warehouses") s.import = raw.import;
    return { ...s, ...viewSearch(warehouseListColumns, raw) };
  },
  loaderDeps: ({ search }) => ({ inactive: search.inactive }),
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(siteListQuery);
    return context.queryClient.ensureInfiniteQueryData(warehouseListQuery(deps.inactive));
  },
  component: WarehousesPage,
});

function WarehousesPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canManage = useCan("inventory.structure.manage");
  const canImport = useCan("core.data.import") && canManage;
  const { company } = useActiveCompany();
  const { data: ctx } = useSessionContext();
  const multiCompany = (ctx?.companies.length ?? 0) > 1;

  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  // All warehouses are loaded (dozens at most): column filters and sort run in the browser.
  const view = listView(warehouseListColumns, search, setSearch);
  const query = filteredQuery(useInfiniteQuery(warehouseListQuery(search.inactive)), clientColumns, view.query.filters, view.query.sort);

  const columns: Column<WarehouseRow>[] = [
    {
      key: "code",
      header: t("warehouses.colCode"),
      width: "minmax(90px,0.6fr)",
      cell: (r, selected) => (
        <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.code}</span>
      ),
    },
    {
      key: "name",
      header: t("warehouses.colName"),
      width: "minmax(180px,1.6fr)",
      cell: (r) => <span className={cn(!r.is_active && "text-ink-muted line-through decoration-ink-faint")}>{r.name}</span>,
    },
    ...(multiCompany
      ? [{ key: "company", header: t("warehouses.colCompany"), width: "minmax(70px,0.5fr)", cell: (r: WarehouseRow) => r.company?.code ?? "—" }]
      : []),
    {
      key: "site",
      header: t("warehouses.colSite"),
      width: "minmax(120px,1fr)",
      className: "text-ink-soft",
      cell: (r) => (r.site ? `${r.site.code} · ${r.site.name}` : "—"),
    },
    {
      key: "type",
      header: t("warehouses.colType"),
      width: "minmax(130px,0.9fr)",
      cell: (r) => <Badge tone={warehouseTypeTone[r.warehouse_type]}>{t(`warehouseType.${r.warehouse_type}`)}</Badge>,
      options: warehouseTypes.map((v) => ({ value: v, label: t(`warehouseType.${v}`) })),
    },
    {
      key: "negative",
      header: t("warehouses.colNegative"),
      width: "minmax(130px,0.8fr)",
      className: "text-ink-muted",
      cell: (r) => (r.allow_negative ? t("warehouses.negativeAllowed") : "—"),
    },
  ];

  const listTools: ListTools<WarehouseRow> = {
    id: "warehouses",
    title: t("warehouses.title"),
    onNew: canManage ? () => setSearch({ warehouse: "new", import: undefined }) : undefined,
    onImport: canImport ? () => setSearch({ import: "warehouses", warehouse: undefined }) : undefined,
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.warehousing.title")} <span aria-hidden="true">/</span> {t("nav.warehousing.warehouses")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("warehouses.title")}</h1>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setSearch({ warehouse: "new", import: undefined })}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("warehouses.newWarehouse")}
          </button>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-[13px] text-ink-soft">
          <input
            type="checkbox"
            checked={search.inactive}
            onChange={(e) => setSearch({ inactive: e.target.checked })}
            className="size-4 accent-(--accent)"
          />
          {t("warehouses.showInactive")}
        </label>
      </div>

      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("warehouses.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.warehouse}
          onSelect={(r) => setSearch({ warehouse: r.id, import: undefined })}
          onClear={() => setSearch({ warehouse: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(warehouseDetailQuery(r.id))}
          emptyTitle={t("warehouses.emptyTitle")}
          emptyBody={t("warehouses.emptyBody")}
          emptyAction={
            canManage ? (
              <button
                type="button"
                onClick={() => setSearch({ warehouse: "new" })}
                className="h-9 rounded-xl bg-accent px-3.5 text-[13px] font-medium text-on-accent hover:bg-accent-hover"
              >
                {t("warehouses.newWarehouse")}
              </button>
            ) : undefined
          }
          minWidth={620}
          tools={listTools}
          view={view}
        />
        <DetailPanel
          label={search.import ? t("import.title") : t("warehouses.detailLabel")}
          open={!!search.warehouse || !!search.import}
          placeholder={<DetailPlaceholder title={t("warehouses.selectPrompt")} hint={t("warehouses.selectPromptHint")} />}
        >
          {search.import ? (
            <ImportPanel
              entities={["warehouses", "sites"]}
              initial={search.import}
              companyId={company?.id}
              onClose={() => setSearch({ import: undefined })}
              invalidate={invalidateAfterImport}
            />
          ) : (
            search.warehouse && (
              <WarehousePanel
                key={search.warehouse}
                warehouseId={search.warehouse}
                onClose={() => setSearch({ warehouse: undefined })}
                onCreated={(id) => setSearch({ warehouse: id })}
              />
            )
          )}
        </DetailPanel>
      </div>
    </div>
  );
}
